import { ComponentFactoryResolver,
  ComponentRef,
  ViewContainerRef,
  ComponentFactory,
  Renderer2,
  ElementRef,
  RendererFactory2,
  ViewEncapsulation } from '@angular/core';
import { WizBaseComponent } from '../../../components/core/wiz-base-component';

/**
 * Helps define intialization properties that can be provided when adding a component dynamically
 */
export interface ComponentInitProperties {
  /**
   * Reference to the existing nodes
   */
  projectableNodes?: any[];
  /**
   * The component index in the container
   */
  index?: number;
  /**
   * The encapsulation type for the component
   */
  encapsulation?: ViewEncapsulation;
  /**
   * Instance properties to be set on the component
   */
  instanceProperties?: {};
  /**
   * Styling properties for the created component
   */
  styleProperties?: {};

  /**
   * A list of css classes
   */
  cssClasses?: string[];
}

/**
 * Singleton class to help create components dynamically and add them into the dom
 */
export class WizComponentManager {
  /**
   * Reference to the Singleton instance
   */
  private static _instance: WizComponentManager;
  /**
   * Reference to the component resolver
   */
  private _resolver: ComponentFactoryResolver;
  /**
   * Reference to the renderer utility used to set style properties on a component
   */
  private _rendererFactory: RendererFactory2;

  /**
   * Map to register every WizBaseComponent currently displayed in the view
   *
   * The key for this map is the elementRef
   */
  private _wizComponents: Map<ElementRef, WizBaseComponent>;

  /**
   * Map to register every WizBaseComponent currently displayed in the view
   *
   * The key for this map is the Component class
   */
  private _elementRefs: Map<WizBaseComponent, ElementRef>;

  constructor() {
    if (!!WizComponentManager._instance) {
      throw new Error('SINGLETON ERROR. This class is a singleton. You must not create another instance');
    }

    // propreties instanciation
    this._elementRefs = new Map<WizBaseComponent, ElementRef>();
    this._wizComponents = new Map<ElementRef, WizBaseComponent>();
  }

  /**
   * Returns the singleton instance
   */
  public static get instance(): WizComponentManager {
    if (!WizComponentManager._instance) {
      WizComponentManager._instance = new WizComponentManager();
    }

    return WizComponentManager._instance;
  }

  /**
   * Sets the reference to the component factory resolver that will be used to dynamically instanciate components
   */
  public set resolver(value: ComponentFactoryResolver) {
    this._resolver = value;
  }

  public set rendererFactory(value: RendererFactory2) {
    this._rendererFactory = value;
  }

  /**
   * Creates an instance of the given component class and adds it to the given container
   *
   * @param pComponentClass The component class that must be instanciated
   * @param pContainer The container in which the user wants the add the newly created component instance
   * @param pInitProperties Optional. Initialization properties to set on the newly create component
   *
   * @returns The ComponentRef object that contains all the information about the component added
   */
  public addChildTo(pComponentClass: any, pContainer: ViewContainerRef, pInitProperties: ComponentInitProperties = { }): ComponentRef<any> {
    // First we create a factory for this component
    const factory: ComponentFactory<any> = this._resolver.resolveComponentFactory(pComponentClass);

    if (pInitProperties.index < 0 || !pInitProperties.index) {
      pInitProperties.index = pContainer.length >= 0 ? pContainer.length : 0;
    }

    // Use the container to instanciate and add the component
    const componentRef: ComponentRef<any> = pContainer.createComponent(factory,
                                  pInitProperties.index,
                                  undefined,
                                  pInitProperties.projectableNodes ? [pInitProperties.projectableNodes] : undefined);

    // If the user provided init properties, we assign them
    if (componentRef.instance && pInitProperties != null) {
      const componentElementRef: ElementRef = componentRef.location;
      // create the renderer helpre for this component
      const renderer: Renderer2 = this.createRenderer(pContainer, componentElementRef, pInitProperties.encapsulation);
      // first, we set the instance properties if they exist
      if (pInitProperties.instanceProperties) {
        for (const instanceProp of Object.keys(pInitProperties.instanceProperties)) {
          renderer.setProperty(componentRef.instance, instanceProp, pInitProperties.instanceProperties[instanceProp]);
        }
      }

      if (componentElementRef) {
        // then, we set the style properties if they exist
        if (pInitProperties.styleProperties) {
          for (const styleprop of Object.keys(pInitProperties.styleProperties)) {
            // make use of the renderer tool to set such values
            renderer.setStyle(componentElementRef.nativeElement, styleprop, pInitProperties.styleProperties[styleprop]);
          }
        }

        // finally, we add classes
        if (pInitProperties.cssClasses) {
          for (const cssclass of pInitProperties.cssClasses) {
            renderer.addClass(componentElementRef.nativeElement, cssclass);
          }
        }
      }
    }

    return componentRef;
  }

  /**
   * Creates the right renderer for the given element depending on encapsulation type
   *
   * @param pContainer The container in which the element exists
   * @param pElement The element to create the renderer for
   * @param pEncapsulation The ViewEncapsulation type. Default to Emulated
   */
  protected createRenderer( pContainer: ViewContainerRef,
                            pElement: ElementRef,
                            pEncapsulation: ViewEncapsulation = ViewEncapsulation.Emulated): Renderer2 {
    if (pEncapsulation === ViewEncapsulation.Emulated || pEncapsulation === ViewEncapsulation.ShadowDom) {
      let parentId: string = null;
      let element: HTMLElement = pContainer.element.nativeElement;

      // if the container given has an id attribute, we use it. Otherwise we look for the parentNode attributes
      if (element) {
        if (!element.attributes || element.attributes.length === 0) {
          element = element.parentElement;
        }

        parentId = element.attributes[0].name;
        // found the parentId we set it on the element. If we don't do this, the
        // viewencapsulation does not work properly and we lose styling via classes
        (pElement.nativeElement as HTMLElement).setAttribute(parentId, null) ;
      }
    }

    // creates the renderr for the component
    return this._rendererFactory.createRenderer(pElement.nativeElement, null);
  }

  /**
   * Removes the given component from the given container
   *
   * @param pComponent ComponentRef to the component to remove/destroy
   * @param pContainer The container that contains the component to destroy
   * @returns True if the component was removed
   */
  public removeChildFrom(pComponent: ComponentRef<any>, pContainer: ViewContainerRef): boolean {
    // First we check that nothing's null
    if (!pComponent || !pContainer) {
      return false;
    }

    // We remove it only if it exists in the given container
    const compoIndex: number = pContainer.indexOf(pComponent.hostView);
    if (compoIndex === -1) {
      return false;
    }

    // Destroy the component with all our might
    pContainer.remove(compoIndex);
    pComponent.destroy();

    return true;
  }

  /**
   * Registers a component that was added to the current view
   *
   * @param pComponent The component class instance that's just been instanciated
   * @param pElementRef The visual element ref associated to the component class
   */
  public registerComponent(pComponent: WizBaseComponent, pElementRef: ElementRef): void {
    this._elementRefs.set(pComponent, pElementRef);
    this._wizComponents.set(pElementRef, pComponent);
  }

  /**
   * Unregisters a component that just got destroyed
   *
   * @param pComponent The component class instance that's juste been destroyed
   * @param pElementRef The visual element ref associated to the component class
   */
  public unregisterComponent(pComponent: WizBaseComponent, pElementRef: ElementRef) {
    this._elementRefs.delete(pComponent);
    this._wizComponents.delete(pElementRef);
  }

  /**
   * Finds the associated ElementRef to a component
   * @param pComponent The key component used to find the ElementRef
   */
  public getElementRef(pComponent: WizBaseComponent): ElementRef {
    return this._elementRefs.get(pComponent);
  }

  /**
   * Finds the component class associated to a Component
   * @param pElement The key element used to find the WizBaseComponent
   */
  public getComponent(pElement: ElementRef): WizBaseComponent {
    return this._wizComponents.get(pElement);
  }
}
